import { Plus, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { cn } from '@/lib/cn'
import { formatNumber } from '@/lib/format'
import {
  emptyLine,
  isLineComplete,
  lineAmount,
  linesTotal,
  type LineDraft,
} from '@/features/finance/lines'

/* The charge editor on a debit note.
 *
 * A debit note IS its lines: the client is being billed for something nobody
 * quoted, so "which charge, and on what authority" is the entire question they
 * are asking. A single total answers none of it, which is why this editor
 * exists rather than one amount field.
 *
 * Money stays a decimal STRING from the keyboard to the wire (AD-9). The only
 * arithmetic here is the running amount beside each row and the total beneath
 * them, and both are DISPLAY — the server recomputes from unit price and
 * quantity, and its figure is the one that reaches paper. Showing a preview the
 * server will re-derive is worth it anyway: a typist who has just entered
 * 330 × 2 wants to see 660 before they submit, and finding out at the PDF is
 * finding out too late.
 */

export function DocumentLines({
  lines,
  currency,
  onChange,
  /** Marks incomplete rows once the sheet has been submitted, matching how the
   *  sheets' own fields wait for `touched` before turning red. */
  showErrors,
}: {
  lines: LineDraft[]
  currency: string
  onChange: (lines: LineDraft[]) => void
  showErrors?: boolean
}) {
  const { t } = useTranslation()

  const update = (key: string, patch: Partial<LineDraft>) =>
    onChange(lines.map((line) => (line.key === key ? { ...line, ...patch } : line)))

  const remove = (key: string) => onChange(lines.filter((line) => line.key !== key))

  const add = () =>
    onChange([
      ...lines,
      // The index alone is not enough for a key: removing row 1 and adding
      // another would reuse its key, and React would keep the removed row's DOM
      // node and its cursor position. The random suffix makes every row a new
      // identity for as long as the sheet is open.
      emptyLine(`line-${lines.length}-${Math.random().toString(36).slice(2, 8)}`),
    ])

  return (
    <div className="flex flex-col gap-2">
      <span className="text-text-body text-[12.5px] leading-none font-normal">
        {t('finance.documents.lines.label')}
      </span>

      {lines.length === 0 ? (
        <p className="text-text-muted text-[12px] leading-snug font-light">
          {t('finance.documents.lines.empty')}
        </p>
      ) : null}

      {lines.map((line, index) => {
        const amount = lineAmount(line)
        const incomplete = showErrors && !isLineComplete(line)

        return (
          <div
            key={line.key}
            className={cn(
              'bg-surface-sunken border-line-hair flex flex-col gap-2 rounded-[var(--radius-md)] border p-2.5',
              incomplete && 'border-negative/45',
            )}
          >
            <div className="flex items-start gap-2">
              <Input
                value={line.description}
                onChange={(e) => update(line.key, { description: e.target.value })}
                placeholder={t('finance.documents.lines.description')}
                aria-label={t('finance.documents.lines.descriptionN', {
                  n: index + 1,
                })}
                className="flex-1"
              />
              {/* Destructive, so it is refused an icon-only treatment nowhere
                  else in the app allows — but a row remove is undone by typing
                  the row again, and a labelled button per line would crowd out
                  the fields. The label lives in aria-label and the tooltip. */}
              <button
                type="button"
                onClick={() => remove(line.key)}
                aria-label={t('finance.documents.lines.remove', {
                  n: index + 1,
                })}
                title={t('finance.documents.lines.remove', { n: index + 1 })}
                className={cn(
                  'text-text-faint hover:text-negative hover:bg-negative-soft',
                  'flex size-9 shrink-0 cursor-pointer items-center justify-center',
                  'rounded-[var(--radius-md)] transition-colors',
                )}
              >
                <Trash2 size={15} aria-hidden />
              </button>
            </div>

            {/* The qualifying line — "4 Containers (Open-top)". Optional, and
                the placeholder says so rather than a separate hint. */}
            <Input
              value={line.note}
              onChange={(e) => update(line.key, { note: e.target.value })}
              placeholder={t('finance.documents.lines.notePlaceholder')}
              aria-label={t('finance.documents.lines.noteN', { n: index + 1 })}
              className="text-[12.5px]"
            />

            <div className="flex items-end gap-2">
              <Input
                inputMode="decimal"
                value={line.unitPrice}
                onChange={(e) => update(line.key, { unitPrice: e.target.value })}
                placeholder={t('finance.documents.lines.unitPrice')}
                aria-label={t('finance.documents.lines.unitPriceN', {
                  n: index + 1,
                })}
                className="asa-num flex-1"
              />
              <span className="text-text-faint pb-2.5 text-[12px]" aria-hidden>
                ×
              </span>
              <Input
                inputMode="decimal"
                value={line.quantity}
                onChange={(e) => update(line.key, { quantity: e.target.value })}
                placeholder={t('finance.documents.lines.quantity')}
                aria-label={t('finance.documents.lines.quantityN', {
                  n: index + 1,
                })}
                className="asa-num w-20 shrink-0"
              />

              {/* The row's own arithmetic, shown as it is typed. A preview of
                  what the server will compute, never the payload. */}
              <span
                className={cn(
                  'asa-num pb-2.5 text-right text-[13px] font-semibold',
                  'text-text-heading min-w-24 shrink-0',
                )}
              >
                {amount === undefined ? '—' : formatNumber(String(amount), 2)}
              </span>
            </div>
          </div>
        )
      })}

      <div className="flex items-center justify-between gap-2 pt-0.5">
        <Button variant="secondary" size="sm" iconLeft={Plus} onClick={add}>
          {t('finance.documents.lines.add')}
        </Button>

        {lines.length > 0 ? (
          <span className="flex items-baseline gap-2">
            <span className="asa-eyebrow">{t('common.fields.total')}</span>
            <span className="asa-num text-text-heading text-[15px] font-semibold">
              {formatNumber(String(linesTotal(lines)), 2)}
              <span className="text-text-muted ml-1 text-[12px] font-normal">
                {currency}
              </span>
            </span>
          </span>
        ) : null}
      </div>
    </div>
  )
}
